import { useEffect, useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import type { ProductEditPageProduct } from "@/routes/productEditTypes";
import {
  resolveProductGating,
  type ProductGating,
  type ProductGatingField,
} from "@/types/ProductGating";

function resolveGatingFromProduct(product: ProductEditPageProduct): ProductGating {
  return resolveProductGating(product.gating);
}

export function ProductEditGatingTab({
  productId,
  product,
}: {
  productId: string;
  product: ProductEditPageProduct;
}) {
  const [isSaving, setIsSaving] = useState(false);
  const [isSavingField, setIsSavingField] = useState(false);
  const [gating, setGating] = useState(() => resolveGatingFromProduct(product));

  useEffect(() => {
    setGating(resolveGatingFromProduct(product));
  }, [product.gating]);

  const saveGating = async ({
    next,
    previous,
  }: {
    next: ProductGating;
    previous: ProductGating;
  }) => {
    setIsSaving(true);
    try {
      const response = await fetch(`/api/products/${productId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ gating: next }),
      });
      const data = await response.json();
      if (!response.ok) {
        setGating(previous);
        alert(data.error || "Failed to update gating");
      }
    } catch (error) {
      console.error("Failed to update gating:", error);
      setGating(previous);
      alert("An error occurred while saving");
    } finally {
      setIsSaving(false);
      setIsSavingField(false);
    }
  };

  const handleEnabledChange = async (checked: boolean) => {
    const previous = gating;
    const next = resolveProductGating({
      enabled: checked,
      fields: gating.fields,
    });
    setGating(next);
    await saveGating({ next, previous });
  };

  const handleFieldEnabledChange = async ({
    fieldKey,
    checked,
  }: {
    fieldKey: ProductGatingField["field"];
    checked: boolean;
  }) => {
    const previous = gating;
    const next = {
      ...gating,
      fields: gating.fields.map((field) =>
        field.field === fieldKey ? { ...field, enabled: checked } : field,
      ),
    };
    setGating(next);
    setIsSavingField(true);
    await saveGating({ next, previous });
  };

  const handleFieldLabelSave = async ({
    fieldKey,
    label,
  }: {
    fieldKey: ProductGatingField["field"];
    label: string;
  }) => {
    const trimmed = label.trim();
    const current = gating.fields.find((field) => field.field === fieldKey);
    if (!current || trimmed === current.label) {
      return;
    }

    const previous = gating;
    const next = {
      ...gating,
      fields: gating.fields.map((field) =>
        field.field === fieldKey ? { ...field, label: trimmed } : field,
      ),
    };
    setGating(next);
    setIsSavingField(true);
    await saveGating({ next, previous });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4 rounded-lg border p-4">
        <div className="space-y-1">
          <Label htmlFor="gatingEnabled">Enable gating</Label>
          <p className="text-sm text-muted-foreground">
            Collect contact information before listeners can access this track.
          </p>
        </div>
        <Switch
          id="gatingEnabled"
          checked={gating.enabled}
          onCheckedChange={(checked) => void handleEnabledChange(checked)}
          disabled={isSaving}
        />
      </div>

      {gating.enabled ? (
        <div className="space-y-4 rounded-lg border p-4">
          <div className="space-y-1">
            <Label>Form fields</Label>
            <p className="text-sm text-muted-foreground">
              Choose which fields to show and customize their labels.
            </p>
          </div>
          <div className="space-y-4">
            {gating.fields.map((field) => (
              <div
                key={field.field}
                className="grid gap-3 rounded-md border p-4 sm:grid-cols-[1fr_auto]"
              >
                <div className="space-y-2">
                  <Label htmlFor={`gating-label-${field.field}`}>
                    {field.label}
                  </Label>
                  <Input
                    id={`gating-label-${field.field}`}
                    defaultValue={field.label}
                    key={`${field.field}-${field.label}`}
                    disabled={isSaving || isSavingField}
                    onBlur={(event) =>
                      void handleFieldLabelSave({
                        fieldKey: field.field,
                        label: event.target.value,
                      })
                    }
                  />
                </div>
                <div className="flex items-end justify-between gap-3 sm:flex-col sm:items-end sm:justify-end">
                  <div className="flex items-center gap-2">
                    <Switch
                      id={`gating-field-${field.field}`}
                      checked={field.enabled}
                      disabled={isSaving || isSavingField}
                      onCheckedChange={(checked) =>
                        void handleFieldEnabledChange({
                          fieldKey: field.field,
                          checked,
                        })
                      }
                    />
                    <Label
                      htmlFor={`gating-field-${field.field}`}
                      className="text-sm text-muted-foreground"
                    >
                      {field.enabled ? "Shown" : "Hidden"}
                    </Label>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}
